import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useParams } from "react-router-dom";
import { base_url } from "Backend/api";
import Icon from "components/AppIcon";
import AppSpinner from "components/ui/AppSpinner";
import toast from "react-hot-toast";

const STATUS_COLORS = {
  done: "bg-emerald-500",
  current: "bg-[#F93209]",
  upcoming: "bg-gray-200",
};

const STATUS_APPEARANCE = {
  confirmed: {
    label: "تم التأكيد",
    className: "bg-[#F3EED6] text-[#6A4A1B]",
    icon: "CheckCheck",
    iconClass: "text-[#1C7D4E]",
  },
  pending: {
    label: "قيد الانتظار",
    className: "bg-[#EFE1D1] text-[#6A4518]",
    icon: "ClipboardList",
    iconClass: "text-[#6A4518]",
  },
  preparing: {
    label: "قيد التنفيذ",
    className: "bg-[#D5E8F5] text-[#175B6D]",
    icon: "Clock3",
    iconClass: "text-[#175B6D]",
  },
  delivering: {
    label: "تم التوصيل",
    className: "bg-[#D6EDDD] text-[#0D6A37]",
    icon: "Truck",
    iconClass: "text-[#0D6A37]",
  },
  delivered: {
    label: "تم التوصيل",
    className: "bg-[#D6EDDD] text-[#0D6A37]",
    icon: "Truck",
    iconClass: "text-[#0D6A37]",
  },
  cancelled: {
    label: "تم الإلغاء",
    className: "bg-[#F2D8DC] text-[#6F1B22]",
    icon: "Ban",
    iconClass: "text-[#6F1B22]",
  },
};

function toNumber(value) {
  const n = Number(value ?? 0);
  return Number.isNaN(n) ? 0 : n;
}



function fmtDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("ar-EG", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function storageUrl(pathOrUrl) {
  if (!pathOrUrl) return "/assets/images/logo.jpeg";
  const s = String(pathOrUrl);
  if (/^https?:\/\//i.test(s)) return s;
  const cleanBase = String(base_url).replace(/\/$/, "");
  if (s.startsWith("/")) return `${cleanBase}${s}`;
  return `${cleanBase}/storage/${s}`;
}

function statusTimeline(order) {
  const items = Array.isArray(order?.status_timeline) ? order.status_timeline : [];
  if (items.length) {
    return items.map((s) => ({
      key: s.key,
      label: s.label,
      state: s.state,
    }));
  }
  return [
    { key: "confirmed", label: "تم تأكيد الطلب", state: "upcoming" },
    { key: "preparing", label: "جاري التحضير", state: "upcoming" },
    { key: "delivering", label: "جاري التوصيل", state: "upcoming" },
    { key: "delivered", label: "تم التوصيل", state: "upcoming" },
  ];
}

function shortTimelineLabel(step) {
  const key = String(step?.key || "").toLowerCase();
  if (key === "confirmed") return "تم تأكيد الطلب";
  if (key === "preparing") return "جاري التحضير";
  if (key === "delivering") return "جاري التوصيل";
  if (key === "delivered") return "تم التوصيل";
  return step?.label || "—";
}

function groupedStores(order) {
  const fromApi = Array.isArray(order?.items_by_store) ? order.items_by_store : [];
  if (fromApi.length) return fromApi;

  const map = {};
  (order?.items || []).forEach((item) => {
    const store =
      item?.dish?.restaurant ||
      item?.product?.market || {
        name: "المتجر",
      };
    const key = store.slug || store.name || `store-${item.id}`;
    if (!map[key]) {
      map[key] = { store, items: [] };
    }
    map[key].items.push(item);
  });
  return Object.values(map);
}

const OrderDetailsPage = () => {
  const navigate = useNavigate();
  const { slug = "" } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDetails = async () => {
      const trimmed = String(slug).trim();
      if (!trimmed) {
        setLoading(false);
        return;
      }
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          toast.error("يجب تسجيل الدخول أولاً");
          setLoading(false);
          return;
        }
        const res = await fetch(
          `${base_url}/api/orders/${encodeURIComponent(trimmed)}/details`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: "application/json",
            },
          }
        );

        const result = await res.json();
        if (res.ok && result?.status && result?.data) {
          setOrder(result.data);
        } else {
          toast.error(result?.message || "تعذر جلب تفاصيل الطلب");
        }
      } catch (e) {
        console.error(e);
        toast.error("حدث خطأ أثناء جلب تفاصيل الطلب");
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [slug]);

  const timeline = useMemo(() => statusTimeline(order), [order]);
  const stores = useMemo(() => groupedStores(order), [order]);
  const normalizedStatus = String(order?.status || "").toLowerCase();
  const statusAppearance =
    STATUS_APPEARANCE[normalizedStatus] || STATUS_APPEARANCE.pending;
  const hideTracking =
    normalizedStatus === "delivered" || normalizedStatus === "cancelled";
  const currentTimelineIndex = timeline.findIndex((s) => s?.state === "current");
  const doneTimelineCount = timeline.filter((s) => s?.state === "done").length;
  const progressSegments =
    currentTimelineIndex > 0
      ? currentTimelineIndex
      : doneTimelineCount > 0
        ? doneTimelineCount - 1
        : 0;
  const maxSegments = Math.max(timeline.length - 1, 1);
  const progressPercent = Math.max(0, Math.min(100, (progressSegments / maxSegments) * 100));
  const edgeOffsetPercent = timeline.length > 0 ? 50 / timeline.length : 0;

  return (
    <>
      <Helmet>
        <title>تفاصيل الطلب - يسلمو</title>
      </Helmet>

      <div className="min-h-screen bg-gray-50 font-[cairo]" dir="rtl">
        <div className="mx-auto w-full max-w-2xl p-3 sm:p-4">
          <div className="mb-3 flex items-center justify-between">
            <button
              onClick={() => navigate("/orders")}
              className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-200"
              aria-label="رجوع"
            >
              <Icon name="ChevronRight" size={20} />
            </button>
            <h1 className="text-base font-medium font-[cairo] text-gray-800">تفاصيل الطلب</h1>
            <span className="w-8" />
          </div>

          {loading && (
            <div className="flex justify-center py-16">
              <AppSpinner />
            </div>
          )}

          {!loading && !order && (
            <div className="rounded-lg bg-white p-6 text-center text-gray-500">
              تعذر عرض تفاصيل الطلب
            </div>
          )}

          {!loading && order && (
            <div className="space-y-3">
              <div className="rounded-[8px] bg-white p-3">
                <div className="mb-1 text-right text-[11px] text-gray-500">
                  {order.order_number || order.slug}
                </div>
                <div className="flex items-center justify-between gap-2">
                  <div
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold ${statusAppearance.className}`}
                  >
                    <Icon
                      name={statusAppearance.icon}
                      size={14}
                      className={statusAppearance.iconClass}
                    />
                    <span>{order.status_label || statusAppearance.label}</span>
                  </div>
                  <div className="text-[11px] text-gray-500">{fmtDate(order.created_at)}</div>

                </div>
                {!hideTracking && (
                  <div className="rounded-md  py-1">
                    <div className="mb-4 flex items-center justify-start gap-1 text-[13px] font-normal text-gray-700">
                      <Icon name="Info" size={16} className="text-[#6A4A1B]" />
                      <span>{order.estimated_delivery_message || "سيتم توصيل الطلب قريبًا"}</span>
                    </div>
                    <div className="relative px-3" dir="rtl">
                      <div
                        className="absolute top-3 h-[2px] bg-[#D9D9D9]"
                        style={{
                          left: `${edgeOffsetPercent}%`,
                          right: `${edgeOffsetPercent}%`,
                        }}
                      />
                      <div
                        className="absolute top-3 h-[2px] bg-[#F93209]"
                        style={{
                          right: `${edgeOffsetPercent}%`,
                          width: `calc((100% - ${edgeOffsetPercent * 2}%) * ${progressPercent / 100})`,
                        }}
                      />
                      <div className="relative flex items-start justify-between gap-2">
                        {timeline.map((step, idx) => {
                          const state = step.state || "upcoming";
                          const isActive = state === "current";
                          const isDone = state === "done" || state === "completed";
                          const label = shortTimelineLabel(step);
                          return (
                            <div key={step.key || idx} className="flex min-w-0 flex-1 flex-col items-center gap-2">
                              <div
                                className={`z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 ${isDone
                                    ? "bg-[#F93209] border-[#F93209]"
                                    : isActive
                                      ? "border-[#F93209] bg-white"
                                      : "border-[#BDBDBD] bg-white"
                                  }`}
                              >
                                {isDone ? (
                                  <Icon name="Check" size={12} className="text-white" />
                                ) : (
                                  <div
                                    className={`h-0.5 w-0.5 rounded-full ${isActive ? "bg-[#F93209]" : "bg-transparent"
                                      }`}
                                  />
                                )}
                              </div>
                              <span
                                className={`max-w-[84px] text-center text-[10px] ${isActive ? "font-bold text-gray-900" : "text-gray-700"
                                  }`}
                              >
                                {label}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="rounded-[8px] bg-white p-3">
                <div className="mb-1 flex items-center gap-1 text-[12px] font-bold text-[#F93209]">
                  <Icon name="MapPin" size={14} />
                  <span>العنوان</span>
                </div>
                <div className="flex flex-rowtext-sm font-semibold text-gray-800 items-center gap-1">
                  <div className="text-[13px] font-semibold">
                    {order.delivery_address?.receiver_name || "—"}
                  </div>
                  <div className="text-[#00B200] text-[8px] font-semibold">
                    ( {order.delivery_address?.title || "—"} )
                  </div>
                </div>
                <div className="text-[11px] text-gray-500">
                  {order.delivery_address?.description ||
                    order.delivery_address?.full_address ||
                    "—"}
                </div>
                <div className="mt-1 flex items-center gap-1 text-[11px] text-gray-500">
                  <Icon name="Phone" size={13} />
                  <span>{order.delivery_address?.receiver_phone || "—"}</span>
                </div>
              </div>

              {stores.map((group, gIdx) => (
                <div key={group?.store?.id || gIdx} className="rounded-[8px] bg-white">
                  <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2">
                    <div className="h-5 w-5 overflow-hidden rounded-full bg-gray-100">
                      <img
                        src={storageUrl(group?.store?.logo || group?.store?.icon || group?.store?.image)}
                        alt=""
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <div className="text-sm font-semibold text-gray-700">{group?.store?.name || "المتجر"}</div>
                  </div>

                  <div className="divide-y divide-gray-100">
                    {(group?.items || []).map((item) => {
                      const product = item.dish || item.product || {};
                      const itemName = product.name || "عنصر";
                      const itemDesc = product.description || "";
                      const image = storageUrl(product.image || product.image_url);
                      const qty = toNumber(item.quantity || 1);
                      const itemPrice = toNumber(item.price);

                      return (
                        <div key={item.id} className="flex items-center gap-3 px-3 py-2">
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-gray-800">{itemName}</div>
                            <div className="line-clamp-1 text-[10px] text-gray-400">{itemDesc || "\u00A0"}</div>
                            <div className="mt-1 text-xs font-semibold text-gray-700">الكمية: {qty}</div>
                          </div>
                          <div className="text-left">
                            <img
                              src={image}
                              alt={itemName}
                              className="mb-1 h-12 w-12 rounded-[8px] object-cover"
                            />
                            <div className="text-xs font-bold text-[#F93209]">{itemPrice.toLocaleString()} ل.س </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              <div className="rounded-[8px] bg-white p-3 text-sm">
                <div className="mb-2 flex items-center gap-1 text-[12px] font-bold text-[#F93209]">
                  <img src="/assets/images/llista.svg" className="h-4 w-4" alt="list" />
                  <span>معلومات الدفع</span>
                </div>
                <div className="flex items-center justify-between text-gray-600">
                  <span>المجموع الفرعي</span>
                  <span> {order.subtotal.toLocaleString()} ل.س</span>
                </div>
                <div className="mt-1 flex items-center justify-between text-gray-600">
                  <span>رسوم التوصيل</span>
                  <span> {order.delivery_fee.toLocaleString()} ل.س</span>
                </div>
                <div className="mt-1 flex items-center justify-between text-gray-600">
                  <span>الخصم</span>
                  <span>-{order.discount_amount.toLocaleString()} ل.س</span>
                </div>
                <div className="mt-2 border-t border-[#F0D9D0] pt-2 text-[15px] font-bold text-gray-800">
                  <div className="flex items-center justify-between">
                    <span>المجموع الاجمالى</span>
                    <span>{order.total_amount.toLocaleString()} ل.س</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default OrderDetailsPage;
